import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import PropTypes from "prop-types";
import {
  useAllocateShippingUnitDetail,
  useCreateAllocateShippingUnit,
  useDetailCustomer,
  useListCustomer,
  useUpdateAllocateShippingUnit,
} from "hooks";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { allocateShippingUnitMode } from "./utils";
import { formValidate } from "helpers/form_helper";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import Select from "react-select";
import { getInitCustomerParams } from "pages/CustomerManagement/utils";

const AllocateShippingUnitsDetailOrCreate = props => {
  const { isOpenModal, dataDetail, toggleModal, mode } = props;
  const { t } = useTranslation(["allocateShippingUnits"]);

  let timeSearchCustomer;

  const transports = dataDetail.transports || [];

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [customerParams, setCustomerParams] = useState(getInitCustomerParams);

  const onSuccessFetchAllocateShippingUnitDetail = data => {
    const transportSelected = transports.find(
      item => item.value == data.transport.id
    );

    setValue("code", data.customer.code, { shouldValidate: false });
    setValue("transport", transportSelected, { shouldValidate: true });

    setCustomerId(data.customer.id);
    setTimeout(() => {
      fetchCustomerDetail();
    }, 100);
  };

  const onFetchListCustomerSuccess = data => {
    const result = [];

    data.items.forEach(item => {
      result.push({ label: item.name, value: item.id });
    });

    setCustomers(result);
  };

  const onFetchCustomerDetailSuccess = data => {
    const customerSelected = { label: data.name, value: data.id };
    setValue("customer", customerSelected, { shouldValidate: false });
  };

  const onUpdateAllocateShippingUnitSuccess = () => {
    dataDetail.fetchAllocateShippingUnits();
  };

  const onCreateAllocateShippingUnitSuccess = () => {
    dataDetail.fetchAllocateShippingUnits();
  };

  const {
    refetch: fetchAllocateShippingUnitDetail,
    isFetching: isLoadingAllocateShippingUnitDetail,
  } = useAllocateShippingUnitDetail(
    {
      allocateShippingUnitId: dataDetail.allocateShippingUnitId,
    },
    onSuccessFetchAllocateShippingUnitDetail
  );

  const { refetch: fetchCustomerDetail, isFetching: isLoadingCustomerDetail } =
    useDetailCustomer({ customerId }, onFetchCustomerDetailSuccess);

  const { isLoading: isLoadingUpdate, mutate: updateAllocateShippingUnit } =
    useUpdateAllocateShippingUnit(onUpdateAllocateShippingUnitSuccess);

  const { isLoading: isLoadingCreate, mutate: createAllocateShippingUnit } =
    useCreateAllocateShippingUnit(onCreateAllocateShippingUnitSuccess);

  const { isFetching: isLoadingCustomer, refetch: fetchCustomer } =
    useListCustomer(
      {
        params: customerParams,
      },
      onFetchListCustomerSuccess
    );

  const initControlFields = {
    display: {
      code: false,
      customer: false,
      transport: false,
      btn_save: false,
      btn_create: false,
    },
    disabled: {
      code: false,
      customer: false,
      transport: false,
      btn_save: false,
      btn_create: false,
    },
  };

  const [controlFields, setControlFields] = useState(initControlFields);

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") }))
      .nullable(),
    customer: yup
      .object()
      .required(t("form.validate.select", { title: t("form.customer_name") }))
      .nullable(),
    transport: yup
      .object()
      .required(t("form.validate.select", { title: t("form.transport_name") }))
      .nullable(),
  });

  const initFormFields = {
    code: "",
    customer: undefined,
    transport: "",
  };

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: { ...initFormFields, lines: [] },
  });

  useEffect(() => {
    if (isOpenModal) {
      setCustomerParams(prev => ({
        ...prev,
        sizePerPage: 100,
        run: true,
        columnFilter: {
          "filter[record_status]": "O",
        }
      }));

      if (
        mode === allocateShippingUnitMode.UPDATE_ALLOCATE_SHIPPING_UNIT_MODE
      ) {
        fetchAllocateShippingUnitDetail();
      }
    }

    switch (mode) {
      case allocateShippingUnitMode.UPDATE_ALLOCATE_SHIPPING_UNIT_MODE:
        setControlFields(prev => ({
          display: {
            ...prev.display,
            code: false,
            customer: true,
            transport: true,
            btn_save: true,
            btn_create: false,
          },
          disabled: {
            ...prev.disabled,
            code: true,
          },
        }));
        break;
      case allocateShippingUnitMode.CREATE_ALLOCATE_SHIPPING_UNIT_MODE:
        setValue("code", " ");
        setControlFields(prev => ({
          display: {
            ...prev.display,
            code: false,
            customer: true,
            transport: true,
            btn_create: true,
            btn_save: false,
          },
          disabled: {
            ...prev.disabled,
            code: false,
          },
        }));
        break;
      default:
        break;
    }

    return () => {
      reset({ ...initFormFields });
      setCustomerParams(getInitCustomerParams());
    };
  }, [isOpenModal, mode]);

  useEffect(() => {
    if (customerParams && customerParams.run) {
      fetchCustomer();
    }
  }, [customerParams]);

  const onSearchCustomer = (keywords, actions) => {
    if (actions.action === "input-change") {
      clearTimeout(timeSearchCustomer)
      timeSearchCustomer = setTimeout(() => {
        setCustomerParams(prev => ({
          ...prev,
          keywords: String(keywords).trim().replaceAll(" ", "+"),
        }));
      }, 500)
    }
  };

  const onSaveSubmit = data => {
    updateAllocateShippingUnit({
      allocateShippingUnitId: dataDetail.allocateShippingUnitId,
      data: {
        customer: data.customer.value,
        transport_id: data.transport.value,
      },
    });
    toggleModal();
  };

  const onCreateSubmit = data => {
    createAllocateShippingUnit({
      data: {
        customer_id: data.customer.value,
        transport_id: data.transport.value,
      },
    });
    toggleModal();
  };

  return (
    <React.Fragment>
      <Modal
        size="xl"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        toggle={toggleModal}
      >
        <div className="modal-content">
          <ModalHeader toggle={toggleModal}>
            {mode ===
            allocateShippingUnitMode.UPDATE_ALLOCATE_SHIPPING_UNIT_MODE
              ? t("title.update")
              : t("title.create")}
          </ModalHeader>
          {isLoadingAllocateShippingUnitDetail || isLoadingCustomerDetail ? (
            <div className="p-5 d-flex justify-content-center">
              <Spinner className="ms-2" color="primary" />
            </div>
          ) : (
            <ModalBody>
              <Row className="row-cols-1 row-cols-lg-2 mt-3">
                {controlFields.display.code && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label Label htmlFor="code">
                        {t("form.code")} <span className="text-danger">*</span>
                      </Label>
                      <input
                        disabled={controlFields.disabled.code}
                        type="text"
                        className={`form-control ${formValidate(
                          errors,
                          touchedFields,
                          "code"
                        )}`}
                        placeholder={t("placeholder.enter", {
                          title: t("form.code"),
                        })}
                        {...register("code")}
                      />
                      {errors.code && (
                        <MessageFormValidate error={errors.code.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.customer && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="customer">
                        {t("form.customer_name")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="customer"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isLoading={isLoadingCustomer}
                            isDisabled={controlFields.disabled.customer}
                            placeholder={t("placeholder.select", {
                              title: t("form.customer_name"),
                            })}
                            options={customers}
                            onChange={data => {
                              field.onChange(data);
                            }}
                            classNamePrefix="select2-selection"
                            onInputChange={(value, actions) => {
                              onSearchCustomer(value, actions);
                            }}
                            onBlur={() => onSearchCustomer("", {action: "input-change"})}
                          />
                        )}
                      />
                      {errors.customer && (
                        <MessageFormValidate error={errors.customer.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.transport && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="transport">
                        {t("form.transport_name")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="transport"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.transport}
                            placeholder={t("placeholder.select", {
                              title: t("form.transport_name"),
                            })}
                            options={transports}
                            onChange={data => {
                              setValue("transport", data);
                            }}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                      {errors.transport && (
                        <MessageFormValidate error={errors.transport.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </ModalBody>
          )}
          <React.Fragment>
            <ModalFooter>
              <Row className="justify-content-end">
                {controlFields.display.btn_save && (
                  <Col className="col-auto">
                    <Button
                      color="primary"
                      className="btn-label"
                      type="button"
                      onClick={() => {
                        handleSubmit(onSaveSubmit)();
                      }}
                      disabled={isLoadingUpdate}
                    >
                      <i className="bx bx-save label-icon"></i>
                      {t("form.save")}
                    </Button>
                  </Col>
                )}

                {controlFields.display.btn_create && (
                  <Col className="col-auto">
                    <Button
                      color="primary"
                      className="btn-label"
                      type="button"
                      onClick={() => {
                        handleSubmit(onCreateSubmit)();
                      }}
                      disabled={isLoadingCreate}
                    >
                      <i className="bx bx-save label-icon"></i>
                      {t("form.create")}
                    </Button>
                  </Col>
                )}
              </Row>
            </ModalFooter>
          </React.Fragment>
        </div>
      </Modal>
    </React.Fragment>
  );
};

AllocateShippingUnitsDetailOrCreate.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  dataDetail: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
};

AllocateShippingUnitsDetailOrCreate.defaultProps = {
  isOpenModal: false,
  dataDetail: {},
};

export default AllocateShippingUnitsDetailOrCreate;
